/**
 * Gate Node Component for React Flow
 *
 * Renders boundary gates (multi-sig approval, access control).
 * Orange/amber styling to indicate control flow point.
 */
import { memo, useState } from "react";
import { Handle, Position, type NodeProps } from "reactflow";
import type { ValueFlowNodeData } from "../../../../utils/schemaToReactFlow";
import { useHoverContext } from "../HoverContext";
import { NodeHoverLabel } from "./NodeHoverLabel";
import { NodeMetadataTooltip } from "./NodeMetadataTooltip";
import { ObservableBadgesContainer } from "./ObservableBadge";

/**
 * Resolve spec_icon path to actual URL
 */
const resolveSpecIconUrl = (specIcon: string): string => {
  const filename = specIcon.split("/").pop();
  return `/assets/sa-elements/${filename}`;
};

/**
 * Default gate/shield icon SVG
 */
const GateIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M12 2l8 4v6c0 5.25-3.5 9.74-8 11-4.5-1.26-8-5.75-8-11V6l8-4z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

/**
 * Multi-sig icon
 */
const MultisigIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="9" cy="7" r="3" />
    <circle cx="15" cy="7" r="3" />
    <path d="M4 21v-2a4 4 0 014-4h2" />
    <path d="M14 21v-2a4 4 0 014-4h2" />
    <path d="M12 12l-1 5h2l-1-5z" />
  </svg>
);

export const GateNodeRF = memo(({ id, data, selected }: NodeProps<ValueFlowNodeData>) => {
  const [imageError, setImageError] = useState(false);
  const { hoveredNodeId } = useHoverContext();
  const isHovered = hoveredNodeId === id;
  const config = data.config as { multisig_n?: number; multisig_m?: number } | undefined;
  const hasMultisig = config?.multisig_n && config?.multisig_m;
  const isMinimal = data.display === "minimal";

  if (isMinimal) {
    // Standard nodes are 80px (2/3 of core's 120px)
    const scale = data.showLabel === false ? Math.min(data.scale ?? 1, 1) : 1;
    const iconSize = Math.max(32, Math.round(80 * scale));
    const fallbackSize = Math.max(24, Math.round(54 * scale));
    const wrapperClass =
      data.showLabel === false
        ? "flex items-center justify-center relative"
        : "flex flex-col items-center gap-2 relative";
    const hiddenHandleClass = "!opacity-0 !w-2 !h-2 !bg-orange-300";
    return (
      <div
        data-testid={`value-flow-node-gate-${id}`}
        className={wrapperClass}
        style={data.showLabel === false ? { width: iconSize, height: iconSize } : undefined}
      >
        {/* Hover components */}
        <NodeHoverLabel name={data.label} description={data.description} visible={isHovered} />
        <NodeMetadataTooltip
          originalData={data.originalData}
          nodeCategory="gate"
          config={data.config}
          isHovered={isHovered}
        />
        {/* All 4 target handles */}
        <Handle type="target" position={Position.Top} id="top" className={hiddenHandleClass} />
        <Handle
          type="target"
          position={Position.Right}
          id="right-in"
          className={hiddenHandleClass}
        />
        <Handle
          type="target"
          position={Position.Bottom}
          id="bottom-in"
          className={hiddenHandleClass}
        />
        <Handle type="target" position={Position.Left} id="left-in" className={hiddenHandleClass} />
        {/* All 4 source handles */}
        <Handle type="source" position={Position.Top} id="top-out" className={hiddenHandleClass} />
        <Handle type="source" position={Position.Right} id="right" className={hiddenHandleClass} />
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          className={hiddenHandleClass}
        />
        <Handle type="source" position={Position.Left} id="left" className={hiddenHandleClass} />

        <div className="relative flex items-center justify-center">
          {data.icon?.spec_icon && !imageError ? (
            <img
              src={resolveSpecIconUrl(data.icon.spec_icon)}
              alt=""
              className="object-contain"
              style={{ width: iconSize, height: iconSize }}
              onError={() => setImageError(true)}
            />
          ) : hasMultisig ? (
            <div className="text-orange-400" style={{ width: fallbackSize, height: fallbackSize }}>
              <MultisigIcon />
            </div>
          ) : (
            <div className="text-orange-400" style={{ width: fallbackSize, height: fallbackSize }}>
              <GateIcon />
            </div>
          )}
          {/* Observable badges from schema ui_hint (gate_binding) */}
          {data.observableBadges && data.observableBadges.length > 0 && (
            <ObservableBadgesContainer
              badges={data.observableBadges}
              position="top-right"
              size="sm"
            />
          )}
        </div>
        {data.showLabel !== false && data.label.trim().length > 0 && (
          <div className="text-base text-sa-text text-center leading-tight max-w-[240px] break-words whitespace-normal font-medium">
            {data.label}
          </div>
        )}
      </div>
    );
  }

  const handleClass = "!bg-orange-400 !border-orange-600 !w-2.5 !h-2.5";

  return (
    <div
      data-testid={`value-flow-node-gate-${id}`}
      className={`
          px-3 py-2 rounded-lg border-2 min-w-[150px]
          bg-gradient-to-br from-orange-500/10 to-sa-warning/10
          ${selected ? "border-orange-400 shadow-lg shadow-orange-500/30" : "border-orange-500/40"}
          transition-all duration-200
        `}
    >
      {/* All 4 target handles */}
      <Handle type="target" position={Position.Top} id="top" className={handleClass} />
      <Handle type="target" position={Position.Right} id="right-in" className={handleClass} />
      <Handle type="target" position={Position.Bottom} id="bottom-in" className={handleClass} />
      <Handle type="target" position={Position.Left} id="left-in" className={handleClass} />
      {/* All 4 source handles */}
      <Handle type="source" position={Position.Top} id="top-out" className={handleClass} />
      <Handle type="source" position={Position.Right} id="right" className={handleClass} />
      <Handle type="source" position={Position.Bottom} id="bottom" className={handleClass} />
      <Handle type="source" position={Position.Left} id="left" className={handleClass} />

      <div className="flex items-center gap-2">
        {/* Icon */}
        <div className="relative flex-shrink-0 w-8 h-8 rounded bg-orange-500/10 flex items-center justify-center text-orange-400">
          {data.icon?.spec_icon && !imageError ? (
            <img
              src={resolveSpecIconUrl(data.icon.spec_icon)}
              alt=""
              className="w-5 h-5 object-contain"
              onError={() => setImageError(true)}
            />
          ) : hasMultisig ? (
            <MultisigIcon />
          ) : (
            <GateIcon />
          )}
          {/* Observable badges from schema ui_hint (gate_binding) */}
          {data.observableBadges && data.observableBadges.length > 0 && (
            <ObservableBadgesContainer
              badges={data.observableBadges}
              position="top-right"
              size="sm"
            />
          )}
        </div>

        {/* Label and multisig info */}
        {data.showLabel !== false && (
          <div className="flex-1 min-w-0">
            <div className="text-base font-medium text-orange-400 leading-tight break-words whitespace-normal">
              {data.label}
            </div>
            {hasMultisig && (
              <div className="text-sm font-mono text-orange-400">
                {config.multisig_m}/{config.multisig_n} sig
              </div>
            )}
            {!hasMultisig && data.description && (
              <div className="text-sm text-orange-400/70 leading-tight break-words whitespace-normal">
                {data.description}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Gate indicator badge */}
      <div className="absolute -top-2 -right-2 px-1.5 py-0.5 text-[10px] font-medium bg-orange-600 text-orange-400 rounded-full">
        GATE
      </div>
    </div>
  );
});

GateNodeRF.displayName = "GateNodeRF";
